import {
  afterNextRender,
  Component,
  computed,
  ElementRef,
  inject,
  Injector,
  input,
  output,
  ChangeDetectionStrategy,
} from '@angular/core';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { BreakpointObserver } from '@angular/cdk/layout';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  SORTABLE_GRID_LABELS,
  SortableGridKeyboard,
} from '@shared/sortable-grid';
import {
  CdkDrag,
  CdkDragDrop,
  CdkDragPlaceholder,
  CdkDropList,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import { TextBlockCategory } from '../models/text-block-category.model';

@Component({
  selector: 'app-text-block-categories-drag-grid',
  standalone: true,
  imports: [CdkDropList, CdkDrag, CdkDragPlaceholder],
  template: `
    <div
      class="drag-grid-container"
      role="application"
      aria-roledescription="sortierbare Liste"
      aria-describedby="text-block-categories-sort-instructions"
      (keydown)="keyboard.onKeydown($event)"
      [attr.aria-label]="
        'Textkategorien neu anordnen. ' + categories().length + ' Einträge.'
      "
    >
      <div
        class="drag-grid"
        cdkDropList
        [cdkDropListData]="categories()"
        (cdkDropListDropped)="onDrop($event)"
        [style.grid-template-columns]="gridTemplateColumns()"
      >
        @for (category of categories(); track category.id; let idx = $index) {
          <div
            class="drag-item"
            cdkDrag
            [cdkDragData]="category"
            [attr.tabindex]="keyboard.tabIndexFor(idx)"
            [attr.data-index]="idx"
            [class.picked]="keyboard.pickedIndex() === idx"
            (focus)="keyboard.onFocus(idx)"
            [attr.aria-label]="'Position ' + (idx + 1) + ': ' + category.name"
            (cdkDragStarted)="onDragStarted(idx + 1, category.name)"
            (cdkDragEnded)="onDragEnded()"
          >
            <div class="drag-item-content">
              <span
                class="material-symbols-outlined drag-handle"
                aria-hidden="true"
              >
                drag_indicator
              </span>
              <span class="position-badge">{{ idx + 1 }}</span>
              <span class="item-name">{{ category.name }}</span>
            </div>
            <div class="cdk-drag-placeholder" *cdkDragPlaceholder></div>
          </div>
        }
      </div>
      <span
        class="cdk-visually-hidden"
        id="text-block-categories-sort-instructions"
      >
        {{ sortLabels.instructions }}
      </span>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      .drag-grid-container {
        height: 100%;
        overflow-y: auto;
        padding: 0 24px 24px 24px;
      }

      .drag-grid {
        display: grid;
        gap: 12px;
        grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
      }

      .drag-item {
        background: var(--md-sys-color-surface-container);
        border-radius: 12px;
        cursor: grab;
        transition:
          box-shadow 150ms ease,
          transform 150ms ease;
      }

      .drag-item:hover {
        box-shadow:
          0 2px 4px -1px rgba(0, 0, 0, 0.1),
          0 4px 5px 0 rgba(0, 0, 0, 0.07),
          0 1px 10px 0 rgba(0, 0, 0, 0.06);
      }

      .drag-item:active {
        cursor: grabbing;
      }

      .drag-item:focus-visible {
        outline: 2px solid var(--md-sys-color-primary);
        outline-offset: 2px;
      }

      .drag-item.picked {
        background: var(--md-sys-color-secondary-container);
        outline: 2px dashed var(--md-sys-color-primary);
        outline-offset: 2px;
      }

      .drag-item-content {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 16px;
      }

      .drag-handle {
        color: var(--md-sys-color-on-surface-variant);
        font-size: 24px;
        flex-shrink: 0;
        font-variation-settings:
          'FILL' 0,
          'wght' 400,
          'GRAD' 0,
          'opsz' 24;
      }

      .drag-item:hover .drag-handle {
        color: var(--md-sys-color-on-surface);
      }

      .position-badge {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 28px;
        height: 28px;
        border-radius: 8px;
        background: var(--md-sys-color-primary-container);
        color: var(--md-sys-color-on-primary-container);
        font-size: 14px;
        font-weight: 600;
        flex-shrink: 0;
      }

      .item-name {
        font-size: 14px;
        font-weight: 500;
        color: var(--md-sys-color-on-surface);
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        flex: 1;
      }

      .cdk-drag-preview {
        box-sizing: border-box;
        border-radius: 12px;
        background: var(--md-sys-color-surface-container-high);
        box-shadow:
          0 5px 5px -3px rgba(0, 0, 0, 0.2),
          0 8px 10px 1px rgba(0, 0, 0, 0.14),
          0 3px 14px 2px rgba(0, 0, 0, 0.12);
      }

      .cdk-drag-preview .drag-item-content {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 16px;
      }

      .cdk-drag-placeholder {
        background: var(--md-sys-color-surface-variant);
        border: 2px dashed var(--md-sys-color-outline);
        border-radius: 12px;
        min-height: 60px;
        opacity: 0.6;
      }

      .cdk-drag-animating {
        transition: transform 250ms cubic-bezier(0, 0, 0.2, 1);
      }

      .cdk-drop-list-dragging .drag-item:not(.cdk-drag-placeholder) {
        transition: transform 250ms cubic-bezier(0, 0, 0.2, 1);
      }

      @media (max-width: 600px) {
        .drag-grid-container {
          padding: 0 16px 16px 16px;
        }
      }

      @media (prefers-reduced-motion: reduce) {
        .drag-item {
          transition: none;
        }

        .cdk-drag-animating {
          transition: none;
        }

        .cdk-drop-list-dragging .drag-item:not(.cdk-drag-placeholder) {
          transition: none;
        }
      }
    `,
  ],
})
export class TextBlockCategoriesDragGridComponent {
  private readonly liveAnnouncer = inject(LiveAnnouncer);
  private readonly breakpointObserver = inject(BreakpointObserver);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly injector = inject(Injector);

  protected readonly sortLabels = SORTABLE_GRID_LABELS;
  protected readonly keyboard = new SortableGridKeyboard({
    items: () => this.categories(),
    name: (category) => category.name,
    reorder: (items) => this.reorderCategories.emit(items),
    announce: (message) => this.liveAnnouncer.announce(message),
    focusItem: (index) =>
      afterNextRender(
        () =>
          this.host.nativeElement
            .querySelector<HTMLElement>(`.drag-item[data-index="${index}"]`)
            ?.focus(),
        { injector: this.injector }
      ),
  });

  categories = input.required<TextBlockCategory[]>();

  reorderCategories = output<TextBlockCategory[]>();

  private readonly isXSmall = toSignal(
    this.breakpointObserver.observe('(max-width: 599px)')
  );
  private readonly isSmall = toSignal(
    this.breakpointObserver.observe('(max-width: 959px)')
  );
  private readonly isMedium = toSignal(
    this.breakpointObserver.observe('(max-width: 1279px)')
  );

  protected readonly gridTemplateColumns = computed(() => {
    if (this.isXSmall()?.matches) return 'repeat(1, minmax(280px, 1fr))';
    if (this.isSmall()?.matches) return 'repeat(2, minmax(280px, 1fr))';
    if (this.isMedium()?.matches) return 'repeat(3, minmax(280px, 1fr))';
    return 'repeat(4, minmax(280px, 1fr))';
  });

  onDragStarted(position: number, name: string): void {
    this.liveAnnouncer.announce(
      `${name} aufgenommen. Aktuelle Position: ${position} von ${this.categories().length}.`
    );
  }

  onDragEnded(): void {
    this.liveAnnouncer.announce('Element abgelegt.');
  }

  onDrop(event: CdkDragDrop<TextBlockCategory[]>): void {
    const categories = [...this.categories()];
    moveItemInArray(categories, event.previousIndex, event.currentIndex);

    const newPosition = event.currentIndex + 1;
    const name = categories[event.currentIndex].name;
    this.liveAnnouncer.announce(
      `${name} verschoben auf Position ${newPosition} von ${categories.length}.`
    );

    this.reorderCategories.emit(categories);
  }
}
